import React, { useEffect, useState } from "react";
import { Instagram, Mail, MapPin, Phone, MessageCircle } from "lucide-react";
import { contactFrom } from "../lib/contact";
import { useLang } from "../context/LanguageContext";
import api from "../lib/api";
import { toast } from "sonner";

export default function Contact() {
  const { t } = useLang();
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  const c = contactFrom(settings);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/messages", form);
      toast.success(t("send_message") + " ✓");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong");
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
      <div className="grid lg:grid-cols-2 gap-16">
        <div>
          <h1 className="serif text-4xl md:text-6xl leading-tight">{t("contact_title")}</h1>
          <p className="text-[#8A857D] leading-relaxed mt-6 max-w-md">{t("contact_sub")}</p>
          <div className="mt-12 space-y-6">
            <div className="flex items-center gap-4 text-[#F2EBE1]">
              <MapPin size={18} className="text-[#C49A6C]" strokeWidth={1.4} />
              <span className="text-sm">{t("location")}</span>
            </div>
            <a href={`mailto:${c.email}`} className="flex items-center gap-4 text-[#F2EBE1] hover:text-[#C49A6C] transition-colors" data-testid="contact-email-link">
              <Mail size={18} className="text-[#C49A6C]" strokeWidth={1.4} />
              <span className="text-sm" dir="ltr">{c.email}</span>
            </a>
            <a href={`tel:+${c.phone.replace(/[^0-9]/g, "")}`} className="flex items-center gap-4 text-[#F2EBE1] hover:text-[#C49A6C] transition-colors" data-testid="contact-phone-link">
              <Phone size={18} className="text-[#C49A6C]" strokeWidth={1.4} />
              <span className="text-sm" dir="ltr">{c.phone}</span>
            </a>
            <a href={`https://wa.me/${c.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-4 text-[#F2EBE1] hover:text-[#C49A6C] transition-colors" data-testid="contact-whatsapp-link">
              <MessageCircle size={18} className="text-[#C49A6C]" strokeWidth={1.4} />
              <span className="text-sm">WhatsApp · <span dir="ltr">+{c.whatsapp}</span></span>
            </a>
            <a href={c.instagram} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="flex items-center gap-4 text-[#F2EBE1] hover:text-[#C49A6C] transition-colors">
              <Instagram size={18} className="text-[#C49A6C]" strokeWidth={1.4} />
              <span className="text-sm" dir="ltr">@{c.instagram.replace(/\/+$/, "").split("/").pop()}</span>
            </a>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-6">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A857D]">{t("full_name")}</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" className="w-full bg-transparent border-b border-[#262421] py-3 mt-2 outline-none focus:border-[#C49A6C] transition-colors text-[#F2EBE1]" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A857D]">{t("email")}</label>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" className="w-full bg-transparent border-b border-[#262421] py-3 mt-2 outline-none focus:border-[#C49A6C] transition-colors text-[#F2EBE1]" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A857D]">{t("message")}</label>
            <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" className="w-full bg-transparent border-b border-[#262421] py-3 mt-2 outline-none focus:border-[#C49A6C] transition-colors text-[#F2EBE1] resize-none" />
          </div>
          <button type="submit" data-testid="contact-submit" className="bg-[#C49A6C] text-[#0B0A08] text-xs uppercase tracking-[0.25em] px-9 py-4 hover:bg-[#E7CBA9] transition-colors">
            {t("send_message")}
          </button>
        </form>
      </div>
    </div>
  );
}
